import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaLayerGroup, FaInfo } from 'react-icons/fa';
import { format } from 'date-fns';
import { AuthContext } from '../../components/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import Modal from '../../components/Modal';
import Swal from 'sweetalert2';
import Select from 'react-select';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const CampaignManagement = () => {
  const { user } = useContext(AuthContext);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyID, setCompanyID] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showSubSchemeModal, setShowSubSchemeModal] = useState(false);
  const [showCompanySelectionModal, setShowCompanySelectionModal] = useState(false);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [subSchemes, setSubSchemes] = useState([]);
  const [stockGroups, setStockGroups] = useState([]);
  const [loadingStockGroups, setLoadingStockGroups] = useState(false);
  const [loadingSubSchemes, setLoadingSubSchemes] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date_from: '',
    date_to: '',
    default_points: 0,
    default_amount: 0
  });
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(false);
  const [hasTriedFetchingCompanies, setHasTriedFetchingCompanies] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [subSchemeForm, setSubSchemeForm] = useState({
    stockGroupId: '',
    points: 0,
    amount: 0
  });
  const [editingSubScheme, setEditingSubScheme] = useState(null);
  const [campaignSubSchemes, setCampaignSubSchemes] = useState([]);
  const [newSubScheme, setNewSubScheme] = useState({
    stockGroupId: '',
    points: 0,
    amount: 0
  });

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || '',
    withCredentials: true
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  useEffect(() => {
    if (!user) return;

    const userData = user.user || user;
    
    // Reset the flag when user changes
    setHasTriedFetchingCompanies(false);
    setAvailableCompanies([]);
    
    console.log('CampaignManagement: Initializing for Admin user with intersection-based access');
    
    // For Admin users, use intersection-based access (admin assigned + domain matching)
    if (user.role === 'Admin' && userData && !hasTriedFetchingCompanies) {
      fetchAdminCompanies();
    } else if (userData && userData.company) {
      // For other user types with direct company assignment
      const id = typeof userData.company === 'object' ? userData.company._id : userData.company;
      setCompanyID(id);
    }
  }, [user]); // Clean dependency array

  // Handle company selection after companies are fetched
  useEffect(() => {
    if (availableCompanies.length > 0 && !companyID) {
      if (availableCompanies.length > 1) {
        setShowCompanySelectionModal(true);
        setCompanyID(availableCompanies[0]._id); // Set default selection
      } else {
        setCompanyID(availableCompanies[0]._id);
      }
    }
  }, [availableCompanies, companyID]);

  const fetchAdminCompanies = async () => {
    if (hasTriedFetchingCompanies || loadingCompanyInfo) {
      console.log('DEBUG Frontend: Skipping fetch - already tried or loading');
      return; // Prevent multiple simultaneous calls
    }
    
    console.log('DEBUG Frontend: Starting to fetch admin companies');
    setLoadingCompanyInfo(true);
    setHasTriedFetchingCompanies(true);
    
    try {
      // Get current domain for filtering company access
      const currentDomain = window.location.hostname;
      
      console.log('DEBUG Frontend: Fetching companies with domain:', currentDomain);
      
      // Fetch companies that are assigned to admin AND match the current domain
      // The API will filter admin's assigned companies to only show domain-matching ones
      const response = await api.get('/admins/me/companies/combined', {
        params: { domain: currentDomain }
      });
      const adminCompanies = response.data;
      
      console.log('DEBUG Frontend: Received response:', adminCompanies);

      if (adminCompanies && adminCompanies.length > 0) {
        console.log('Received companies from intersection API:', adminCompanies);
        setAvailableCompanies(adminCompanies);
        // Note: Company selection will be handled by the useEffect that watches availableCompanies
      } else {
        console.warn('No companies returned from intersection API');
        setError('No companies available that match both your admin permissions and current access context. Please contact support.');
      }
    } catch (err) {
      console.error('Error fetching admin companies:', err);
      
      // Provide more specific error messages and fallback options
      if (err.response?.status === 404) {
        setError('Admin not found. Please check your account status or contact support.');
      } else if (err.response?.status === 403) {
        setError('Access denied. You may not have permission to access company information.');
      } else if (err.response?.status === 500) {
        setError('Server error occurred while fetching companies. Please try again later.');
      } else {
        setError('Failed to fetch available companies. Please contact support.');
      }
    } finally {
      setLoadingCompanyInfo(false);
    }
  };

  const handleCompanySelect = (companyId) => {
    setCompanyID(companyId);
    setShowCompanySelectionModal(false);
    localStorage.setItem('selectedCompanyID', companyId);
  };

  const getCompaniesForSelection = () => {
    return availableCompanies && availableCompanies.length > 0 ? availableCompanies : [];
  };

  useEffect(() => {
    if (companyID) {
      fetchCampaigns();
    }
  }, [companyID]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const effectiveCompanyID = companyID || localStorage.getItem('selectedCompanyID') ||
        (user?.user?.company?._id || user?.user?.company);

      if (!effectiveCompanyID) {
        console.log('No company ID available, cannot fetch campaigns');
        setCampaigns([]);
        setError('Please select a company to view campaigns');
        return;
      }

      const response = await axios.get(`${API_BASE}/campaigns`, {
        params: { companyID: effectiveCompanyID }
      });
      
      // Handle both direct data array and data.campaigns format
      const campaignData = Array.isArray(response.data) ? response.data : (response.data.campaigns || []);
      setCampaigns(campaignData);
      
      if (campaignData.length === 0) {
        console.log('No campaigns found for company ID:', effectiveCompanyID);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError(`Error fetching campaigns: ${err.response?.data?.msg || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchStockGroups = async () => {
    setLoadingStockGroups(true);
    try {
      const effectiveCompanyID = companyID || localStorage.getItem('selectedCompanyID') ||
        (user?.user?.company?._id || user?.user?.company);

      const response = await axios.get(`${API_BASE}/tally-stock-groups?companyID=${effectiveCompanyID}`);
      setStockGroups(response.data || []);
    } catch (err) {
      console.error('Error fetching stock groups:', err);
      showAlert('danger', `Error fetching stock groups: ${err.response?.data?.msg || err.message}`);
    } finally {
      setLoadingStockGroups(false);
    }
  };

  const fetchSubSchemes = async (campaignId) => {
    setLoadingSubSchemes(true);
    try {
      const effectiveCompanyID = companyID || localStorage.getItem('selectedCompanyID') ||
        (user?.user?.company?._id || user?.user?.company);

      // Fix: Change "sub-schemes" to "sub-scheme" to match backend route
      const response = await axios.get(
        `${API_BASE}/campaigns/${campaignId}/sub-scheme?companyID=${effectiveCompanyID}`
      );
      // Ensure we're always setting an array to subSchemes
      setSubSchemes(Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []));
    } catch (err) {
      console.error('Error fetching sub schemes:', err);
      showAlert('danger', `Error fetching stock group rules: ${err.response?.data?.msg || err.message}`);
    } finally {
      setLoadingSubSchemes(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubSchemeChange = (e) => {
    const { name, value } = e.target;
    setSubSchemeForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddNew = () => {
    setCurrentCampaign(null);
    setFormData({
      name: '',
      date_from: format(new Date(), 'yyyy-MM-dd'),
      date_to: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      default_points: 0,
      default_amount: 0
    });
    setCampaignSubSchemes([]);
    fetchStockGroups();
    setShowModal(true);
  };

  const handleEdit = (campaign) => {
    setCurrentCampaign(campaign);
    setFormData({
      name: campaign.name,
      date_from: format(new Date(campaign.date_from), 'yyyy-MM-dd'),
      date_to: format(new Date(campaign.date_to), 'yyyy-MM-dd'),
      default_points: campaign.default_points,
      default_amount: campaign.default_amount
    });

    fetchSubSchemes(campaign._id).then(() => {
      setCampaignSubSchemes(subSchemes || []);
    });

    fetchStockGroups();
    setShowModal(true);
  };

  const handleManageSubSchemes = async (campaign) => {
    setCurrentCampaign(campaign);
    setShowSubSchemeModal(true);
    fetchStockGroups();
    fetchSubSchemes(campaign._id);
  };

  const handleAddSubScheme = async (e) => {
    e.preventDefault();

    if (!currentCampaign || !currentCampaign._id) {
      showAlert('danger', 'Campaign information is missing. Please try again.');
      return;
    }

    if (!subSchemeForm.stockGroupId) {
      showAlert('danger', 'Please select a stock group');
      return;
    }

    setLoadingSubSchemes(true);
    try {
      const effectiveCompanyID = companyID || localStorage.getItem('selectedCompanyID') ||
        (user?.user?.company?._id || user?.user?.company);

      if (!effectiveCompanyID) {
        showAlert('danger', 'No company selected. Please select a company first.');
        setLoadingSubSchemes(false);
        return;
      }

      // Get stock group name to ensure it's passed correctly
      const stockGroupName = getStockGroupName(subSchemeForm.stockGroupId);
      
      console.log('Adding stock group with name:', stockGroupName);
      
      // Send request with companyID in both query params and request body
      const response = await axios.post(
        `${API_BASE}/campaigns/${currentCampaign._id}/sub-scheme`,
        {
          stockGroupId: subSchemeForm.stockGroupId,
          stockGroupName: stockGroupName !== 'Unknown' && stockGroupName !== 'Default Group' ? stockGroupName : null,
          points: parseFloat(subSchemeForm.points),
          amount: parseFloat(subSchemeForm.amount),
          companyID: effectiveCompanyID
        },
        {
          params: {
            companyID: effectiveCompanyID
          }
        }
      );

      // Reset form regardless of response structure
      setSubSchemeForm({
        stockGroupId: '',
        points: 0,
        amount: 0
      });

      // Refresh the data
      fetchSubSchemes(currentCampaign._id);
      showAlert('success', 'Stock group rule added successfully');
    } catch (err) {
      console.error('Error adding sub scheme:', err);
      showAlert('danger', `Error adding stock group rule: ${err.response?.data?.msg || err.message}`);
    } finally {
      setLoadingSubSchemes(false);
    }
  };

  const handleDeleteSubScheme = async (subSchemeId) => {
    confirmAction(
      'Delete Stock Group Rule?',
      'Are you sure you want to delete this stock group rule?',
      async () => {
        setLoadingSubSchemes(true);
        try {
          const effectiveCompanyID = companyID || localStorage.getItem('selectedCompanyID') ||
            (user?.user?.company?._id || user?.user?.company);

          if (!effectiveCompanyID) {
            showAlert('danger', 'No company selected. Please select a company first.');
            setLoadingSubSchemes(false);
            return;
          }

          const response = await api.delete(
            `${API_BASE}/campaigns/${currentCampaign._id}/sub-scheme/${subSchemeId}?companyID=${effectiveCompanyID}`
          );
          // Ensure we're always setting an array to subSchemes
          setSubSchemes(Array.isArray(response.data) ? response.data : []);
          showAlert('success', 'Stock group rule deleted successfully');
        } catch (err) {
          console.error('Error deleting sub-scheme:', err);
          showAlert('danger', `Failed to delete sub-scheme: ${err.response?.data?.msg || err.message}`);
        } finally {
          setLoadingSubSchemes(false);
        }
      }
    );
  };

  const handleEditSubScheme = (subScheme) => {
    setEditingSubScheme(subScheme);
    setSubSchemeForm({
      stockGroupId: subScheme.stockGroupId || subScheme.stockGroups,
      points: subScheme.points,
      amount: subScheme.amount
    });

    setTimeout(() => {
      document.getElementById('edit-subscheme-form').scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const validatePointsAmount = (points, amount) => {
    points = parseFloat(points);
    amount = parseFloat(amount);

    if (points > 1000 && amount < 100) {
      return {
        valid: false,
        message: `The configuration appears to be reversed. Did you mean ${amount} points per ₹${points}? Please confirm your values.`
      };
    }
    return { valid: true };
  };

  const handleUpdateSubScheme = async (e) => {
    e.preventDefault();
    if (!editingSubScheme) {
      showAlert('warning', 'No sub-scheme selected for editing');
      return;
    }

    // First, check if we have a valid campaign ID
    if (!currentCampaign || !currentCampaign._id) {
      showAlert('danger', 'Campaign information is missing. Please try again.');
      return;
    }

    const validation = validatePointsAmount(editingSubScheme.points, editingSubScheme.amount);

    if (!validation.valid) {
      if (window.confirm(validation.message + " Would you like to swap these values?")) {
        setEditingSubScheme({
          ...editingSubScheme,
          points: editingSubScheme.amount,
          amount: editingSubScheme.points
        });
        return;
      }
    }

    setLoadingSubSchemes(true);
    try {
      const effectiveCompanyID = companyID || localStorage.getItem('selectedCompanyID') ||
        (user?.user?.company?._id || user?.user?.company);

      if (!effectiveCompanyID) {
        showAlert('danger', 'No company selected. Please select a company first.');
        setLoadingSubSchemes(false);
        return;
      }

      // Include companyID in both the URL query parameters and the request body
      const response = await axios.put(
        `${API_BASE}/campaigns/${currentCampaign._id}/sub-scheme/${editingSubScheme._id}`,
        {
          points: parseFloat(editingSubScheme.points),
          amount: parseFloat(editingSubScheme.amount),
          companyID: effectiveCompanyID
        },
        {
          params: { companyID: effectiveCompanyID }
        }
      );

      fetchSubSchemes(currentCampaign._id);
      setEditingSubScheme(null);
      showAlert('success', 'Stock group rule updated successfully');
    } catch (err) {
      console.error('Error updating sub scheme:', err);
      showAlert('danger', `Error updating stock group rule: ${err.response?.data?.msg || err.message}`);
    } finally {
      setLoadingSubSchemes(false);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  const validation = validatePointsAmount(formData.default_points, formData.default_amount);

  if (!validation.valid) {
    if (window.confirm(validation.message + " Would you like to swap these values?")) {
      setFormData({
        ...formData,
        default_points: formData.default_amount,
        default_amount: formData.default_points
      });
      return;
    }
  }
  

  try {
    const effectiveCompanyID = companyID || localStorage.getItem('selectedCompanyID') ||
      (user?.user?.company?._id || user?.user?.company);

    if (!effectiveCompanyID) {
      showAlert('danger', 'No company selected. Please select a company first.');
      return;
    }

      const payload = {
        ...formData,
        companyID: effectiveCompanyID,
        stockGroupPoints: campaignSubSchemes.map(scheme => ({
          stockGroupId: scheme.stockGroupId || scheme.stockGroups,
          stockGroupName: scheme.stockGroupName || getStockGroupName(scheme.stockGroupId || scheme.stockGroups),
          points: parseFloat(scheme.points),
          amount: parseFloat(scheme.amount)
        }))
      };

      let response;
      if (currentCampaign) {
        response = await axios.put(
          `${API_BASE}/campaigns/${currentCampaign._id}?companyID=${effectiveCompanyID}`,
          payload
        );
      } else {
        response = await axios.post(
          `${API_BASE}/campaigns?companyID=${effectiveCompanyID}`,
          payload
        );
      }

      if (response.data.success || response.data) {
        fetchCampaigns();
        setShowModal(false);
        showAlert('success', `Campaign ${currentCampaign ? 'updated' : 'added'} successfully`);
      }
    } catch (err) {
      console.error('Error submitting campaign:', err);
      showAlert('danger', `Error: ${err.response?.data?.msg || err.message}`);
    }
  };

  const handleDelete = async (id) => {
    confirmAction(
      'Delete Campaign?',
      'Are you sure you want to delete this campaign? This action cannot be undone.',
      async () => {
        setLoading(true);
        try {
          const effectiveCompanyID = companyID || localStorage.getItem('selectedCompanyID') ||
            (user?.user?.company?._id || user?.user?.company);

          await api.delete(`${API_BASE}/campaigns/${id}?companyID=${effectiveCompanyID}`);
          setCampaigns(campaigns.filter(camp => camp._id !== id));
          showAlert('success', 'Campaign deleted successfully!');
        } catch (err) {
          console.error('Error deleting campaign:', err);
          showAlert('danger', `Failed to delete campaign: ${err.response?.data?.msg || err.message}`);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const showAlert = (type, message) => {
    const swalIcon = type === 'success' ? 'success' :
      type === 'warning' ? 'warning' :
        type === 'danger' ? 'error' : 'info';

    Swal.fire({
      icon: swalIcon,
      title: type.charAt(0).toUpperCase() + type.slice(1),
      text: message,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true
    });
  };

  const confirmAction = (title, text, action) => {
    Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, proceed!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        action();
      }
    });
  };

  const getCampaignStatus = (campaign) => {
    const today = new Date();
    const startDate = new Date(campaign.date_from);
    const endDate = new Date(campaign.date_to);

    if (today < startDate) {
      return { status: 'Upcoming', className: 'bg-blue-100 text-blue-800' };
    } else if (today > endDate) {
      return { status: 'Expired', className: 'bg-red-100 text-red-800' };
    } else {
      return { status: 'Active', className: 'bg-green-100 text-green-800' };
    }
  };

  // Stock group name cache to improve performance
  const stockGroupNameCache = {};
  
  const getStockGroupName = (id) => {
    if (!id) return 'Default'; // Handle null or undefined

    // Check cache first
    const cacheKey = typeof id === 'object' ? 
      (id._id ? id._id.toString() : JSON.stringify(id)) : 
      id.toString();
    
    if (stockGroupNameCache[cacheKey]) {
      return stockGroupNameCache[cacheKey];
    }
    
    // Enable detailed logging for debugging
    console.log('Looking up stock group', typeof id, id);
    let result = null;
    
    // Handle case where id is an array
    if (Array.isArray(id) && id.length > 0) {
      // Try first element if it's an array
      result = getStockGroupName(id[0]);
      if (result !== 'Unknown') {
        stockGroupNameCache[cacheKey] = result;
        return result;
      }
    }
    
    // Direct lookup from stockGroups cache if we already fetched them
    if (stockGroups && stockGroups.length > 0) {
      // If id is already a stock group object with a Name property
      if (typeof id === 'object' && id !== null) {
        if (id.Name) {
          result = id.Name;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }
        if (id.name) {
          result = id.name;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }
        if (id.GROUP_NAME) {
          result = id.GROUP_NAME;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }
        if (id.groupName) {
          result = id.groupName;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }
        if (id.group_name) {
          result = id.group_name;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }
        if (id.value && id.label) {
          result = id.label;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }
      }
      
      // If id is an object with _id field
      if (typeof id === 'object' && id !== null && id._id) {
        const idString = typeof id._id === 'string' ? id._id : id._id.toString();
        console.log('Using _id for lookup:', idString);
        id = id._id; // Use the _id for lookup
      }

      // Try direct ID match first (most common case)
      const directMatch = stockGroups.find(sg => {
        if (!sg || !sg._id) return false;
        
        // Compare directly or as strings
        if (sg._id === id) return true;
        if (typeof sg._id === 'object' && sg._id.$oid && typeof id === 'string' && id.includes(sg._id.$oid)) return true;
        if (typeof sg._id.toString === 'function' && 
            typeof id?.toString === 'function' && 
            sg._id.toString() === id.toString()) {
          return true;
        }
        return false; 
      });
      
      if (directMatch) {
        console.log('Found direct match:', directMatch.Name);
        result = directMatch.Name;
        stockGroupNameCache[cacheKey] = result;
        return result;
      }
      
      // Try by object ID in various formats
      const objectIdMatch = stockGroups.find(sg => {
        if (!sg || !sg._id) return false;
        
        // MongoDB ObjectId with $oid
        if (sg._id && sg._id.$oid && typeof id === 'object' && id !== null && id.$oid) {
          return sg._id.$oid === id.$oid;
        }
        
        // String representation of ObjectId
        if (typeof id === 'string') {
          // Try direct match
          if (String(sg._id) === id) return true;
          
          // Try string comparison
          if (typeof sg._id.toString === 'function' && sg._id.toString() === id) {
            return true;
          }
          
          // Try ID pattern matching (for MongoDB ObjectIds)
          if (typeof id === 'string' && id.match(/^[0-9a-fA-F]{24}$/)) {
            if (typeof sg._id.toString === 'function' && sg._id.toString().match(/^[0-9a-fA-F]{24}$/)) {
              return sg._id.toString() === id;
            }
          }
          
          // Try shortened ID comparison (sometimes IDs are truncated)
          if (typeof sg._id.toString === 'function') {
            const sgIdStr = sg._id.toString();
            return sgIdStr.startsWith(id) || id.startsWith(sgIdStr);
          }
        }
        
        return false;
      });
      
      if (objectIdMatch) {
        console.log('Found ObjectId match:', objectIdMatch.Name);
        result = objectIdMatch.Name;
        stockGroupNameCache[cacheKey] = result;
        return result;
      }

      // Try exact name match
      if (typeof id === 'string') {
        const nameMatch = stockGroups.find(sg => 
          sg.Name && sg.Name === id
        );
        
        if (nameMatch) {
          console.log('Found name match:', nameMatch.Name);
          result = nameMatch.Name;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }
        
        // Try case-insensitive name match
        const caseInsensitiveNameMatch = stockGroups.find(sg => 
          sg.Name && sg.Name.toLowerCase() === id.toLowerCase()
        );
        
        if (caseInsensitiveNameMatch) {
          console.log('Found case-insensitive name match:', caseInsensitiveNameMatch.Name);
          result = caseInsensitiveNameMatch.Name;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }
        
        // Try partial ID match 
        const partialIdMatch = stockGroups.find(sg => 
          sg._id && typeof sg._id.toString === 'function' && sg._id.toString().includes(id)
        );
        
        if (partialIdMatch) {
          console.log('Found partial ID match:', partialIdMatch.Name);
          result = partialIdMatch.Name;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }
        
        // Try partial name match
        const partialNameMatch = stockGroups.find(sg => 
          sg.Name && sg.Name.toLowerCase().includes(id.toLowerCase())
        );
        
        if (partialNameMatch) {
          console.log('Found partial name match:', partialNameMatch.Name);
          result = partialNameMatch.Name;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }

        // Try any field match (desperate)
        const anyMatch = stockGroups.find(sg => {
          // Try to match any string field
          for (const key in sg) {
            if (typeof sg[key] === 'string' && 
               (sg[key] === id || sg[key].toLowerCase() === id.toLowerCase())) {
              return true;
            }
          }
          return false;
        });
        
        if (anyMatch) {
          console.log('Found match on random field:', anyMatch.Name);
          result = anyMatch.Name;
          stockGroupNameCache[cacheKey] = result;
          return result;
        }
      }
    }
    
    // If we reach here, we couldn't find a match
    console.warn('Could not find stock group for:', id);
    
    // Try to return something meaningful
    if (typeof id === 'string' && id.trim() !== '') {
      // Use the ID string directly if it's not just a MongoDB ObjectId
      if (!id.match(/^[0-9a-fA-F]{24}$/)) {
        result = id;
        stockGroupNameCache[cacheKey] = result;
        return result;
      } 
      
      // Try to fetch the stock group if it's not in the cache
      // This is a last resort and might trigger multiple API calls,
      // but it's better than showing "Unknown"
      fetchStockGroupByIdDirectly(id)
        .then(name => {
          if (name) {
            stockGroupNameCache[cacheKey] = name;
            // We can't update the UI here directly, but the cache will
            // be used next time this ID is requested
          }
        })
        .catch(() => {});

      return id.length > 8 ? `Stock Group (${id.substring(0, 8)}...)` : `Stock Group (${id})`;
    } else if (typeof id === 'object' && id !== null) {
      // If it's an object, try to extract useful information
      if (id.Name) return id.Name;
      if (id.name) return id.name;
      if (id.GROUP_NAME) return id.GROUP_NAME;
      if (id.groupName) return id.groupName;
      if (id.group_name) return id.group_name;
      if (id.label) return id.label;
      if (id.value && typeof id.value === 'string') return id.value;
      if (id._id) {
        const idStr = typeof id._id === 'string' ? id._id : id._id.toString();
        
        // Try to fetch the stock group if it's not in the cache
        fetchStockGroupByIdDirectly(idStr)
          .then(name => {
            if (name) {
              stockGroupNameCache[cacheKey] = name;
            }
          })
          .catch(() => {});
        
        return `Stock Group (${idStr.substring(0, 6)}...)`;
      }
      
      // For debugging purposes, log the keys of the object
      console.log('Object keys:', Object.keys(id));
      return 'Stock Group'; 
    }
    
    return 'Default Group';
  };

  // Helper function to fetch a stock group by ID directly from the API
  const fetchStockGroupByIdDirectly = async (id) => {
    try {
      const effectiveCompanyID = companyID || localStorage.getItem('selectedCompanyID') ||
        (user?.user?.company?._id || user?.user?.company);
      
      if (!effectiveCompanyID) return null;
      
      // Make a direct API call to get the stock group
      const response = await axios.get(`${API_BASE}/tally-stock-groups/${id}?companyID=${effectiveCompanyID}`);
      
      if (response.data && response.data.Name) {
        console.log('Fetched stock group directly:', response.data.Name);
        return response.data.Name;
      }
      return null;
    } catch (err) {
      console.error('Error fetching stock group directly:', err);
      return null;
    }
  };

  const handleSubSchemeInputChange = (e) => {
    const { name, value } = e.target;
    setNewSubScheme(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddSubSchemeToForm = () => {
    if (!newSubScheme.stockGroupId) {
      showAlert('warning', 'Please select a stock group');
      return;
    }

    const exists = campaignSubSchemes.some(
      scheme => scheme.stockGroupId === newSubScheme.stockGroupId ||
        scheme.stockGroups === newSubScheme.stockGroupId
    );

    if (exists) {
      showAlert('warning', 'This stock group is already added');
      return;
    }

    const stockGroup = stockGroups.find(sg => sg._id === newSubScheme.stockGroupId);
    setCampaignSubSchemes([
      ...campaignSubSchemes,
      {
        stockGroupId: newSubScheme.stockGroupId,
        stockGroupName: stockGroup ? stockGroup.Name : 'Unknown',
        points: parseFloat(newSubScheme.points || formData.default_points),
        amount: parseFloat(newSubScheme.amount || formData.default_amount)
      }
    ]);

    setNewSubScheme({
      stockGroupId: '',
      points: formData.default_points,
      amount: formData.default_amount
    });
  };

  const handleRemoveSubSchemeFromForm = (index) => {
    setCampaignSubSchemes(campaignSubSchemes.filter((_, i) => i !== index));
  };

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Campaign Management</h2>

        {getCompaniesForSelection().length > 1 && (
          <button
            onClick={() => setShowCompanySelectionModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <span>Switch Company</span>
          </button>
        )}
      </div>

      {showCompanySelectionModal && (
        <Modal
          title="Select Company"
          onClose={() => {
            const companies = getCompaniesForSelection();
            if (!companyID && companies && companies.length > 0) {
              handleCompanySelect(companies[0]._id);
            } else {
              setShowCompanySelectionModal(false);
            }
          }}
          showFooter={false}
        >
          <div className="p-4">
            <p className="mb-4 text-gray-700">
              Multiple companies are available for your admin account. Please select which company you want to manage campaigns for:
            </p>
            <div className="space-y-2">
              {getCompaniesForSelection().map(company => {
                const isSelected = company._id === companyID;
                return (
                  <button
                    key={company._id}
                    className={`w-full py-2 px-4 border ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} rounded-md shadow-sm hover:bg-gray-50 text-left flex justify-between items-center`}
                    onClick={() => handleCompanySelect(company._id)}
                  >
                    <div>
                      <div className="font-medium text-gray-800">{company.name}</div>
                    </div>
                    {isSelected && (
                      <div className="flex-shrink-0 h-6 w-6 text-blue-600">
                        <FaCheck />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={() => {
                  const companies = getCompaniesForSelection();
                  if (!companyID && companies && companies.length > 0) {
                    handleCompanySelect(companies[0]._id);
                  } else {
                    setShowCompanySelectionModal(false);
                  }
                }}
              >
                Confirm Selection
              </button>
            </div>
          </div>
        </Modal>
      )}

      {companyID && (
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-8 relative z-10">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <FaLayerGroup className="text-white text-xl" />
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Current Company</div>
                <div className="text-lg font-bold text-gray-900">
                  {(() => {
                    const companies = getCompaniesForSelection();
                    const selectedCompany = companies.find(c => c._id === companyID);
                    if (selectedCompany) return selectedCompany.name;

                    const userData = user?.user || user;
                    if (userData?.company?.name) return userData.company.name;

                    return "Unknown Company";
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {loadingCompanyInfo ? (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-3 border-white border-t-transparent"></div>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Loading Company Information</h3>
            <p className="text-gray-600">Please wait while we retrieve your company details...</p>
          </div>
        </div>
      ) : loading ? (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-red-200 p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center">
                <FaTimes className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Something went wrong</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                onClick={fetchCampaigns}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
          {/* Modern Header Section */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 shadow-xl">
            <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                <div className="flex-1">
                  <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                    Campaign Management
                  </h1>
                  <p className="text-blue-100 text-lg">
                    Create and manage marketing campaigns with reward schemes
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={handleAddNew}
                    className="bg-white bg-opacity-20 backdrop-blur-sm border border-white border-opacity-30 hover:bg-opacity-30 text-white font-semibold py-3 px-6 rounded-xl flex items-center space-x-3 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    <FaPlus className="text-lg" />
                    <span>New Campaign</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {loading && campaigns.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {campaigns.length === 0 ? (
                <div className="text-center py-16">
                  <div className="max-w-md mx-auto">
                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg border border-blue-100">
                      <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <FaLayerGroup className="text-3xl text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">No Campaigns Yet</h3>
                      <p className="text-gray-600 mb-6">
                        Create your first marketing campaign to start managing reward schemes and engage with customers.
                      </p>
                      <button
                        onClick={handleAddNew}
                        className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl flex items-center space-x-2 mx-auto transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                      >
                        <FaPlus />
                        <span>Create First Campaign</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {campaigns.map((campaign) => {
                    const status = getCampaignStatus(campaign);

                    return (
                      <div 
                        key={campaign._id} 
                        className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 overflow-hidden group"
                      >
                        {/* Campaign Header with Gradient */}
                        <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-600 p-6 text-white relative overflow-hidden">
                          <div className="absolute inset-0 bg-black bg-opacity-10"></div>
                          <div className="relative z-10">
                            <div className="flex justify-between items-start mb-4">
                              <h3 className="text-xl font-bold truncate pr-4">{campaign.name}</h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                status.status === 'Active' 
                                  ? 'bg-green-500 bg-opacity-20 text-green-100 border border-green-300' 
                                  : status.status === 'Upcoming'
                                  ? 'bg-yellow-500 bg-opacity-20 text-yellow-100 border border-yellow-300'
                                  : 'bg-gray-500 bg-opacity-20 text-gray-100 border border-gray-300'
                              }`}>
                                {status.status}
                              </span>
                            </div>
                            
                            {/* Duration */}
                            <div className="text-blue-100 text-sm">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-medium">Start:</span>
                                <span>{format(new Date(campaign.date_from), 'MMM dd, yyyy')}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">End:</span>
                                <span>{format(new Date(campaign.date_to), 'MMM dd, yyyy')}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Decorative Elements */}
                          <div className="absolute -top-4 -right-4 w-24 h-24 bg-white bg-opacity-10 rounded-full"></div>
                          <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white bg-opacity-5 rounded-full"></div>
                        </div>

                        {/* Campaign Body */}
                        <div className="p-6">
                          {/* Rewards Information */}
                          <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-6">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                              Default Rewards
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">{campaign.default_points}</div>
                                <div className="text-xs text-gray-600 font-medium">Points</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">₹{campaign.default_amount}</div>
                                <div className="text-xs text-gray-600 font-medium">Amount</div>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-col space-y-3">
                            <button
                              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center space-x-2 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                              onClick={() => handleManageSubSchemes(campaign)}
                            >
                              <FaLayerGroup className="text-lg" />
                              <span>Manage Stock Rules</span>
                            </button>
                            
                            <div className="flex space-x-3">
                              <button
                                onClick={() => handleEdit(campaign)}
                                className="flex-1 bg-gray-100 hover:bg-blue-50 text-blue-600 hover:text-blue-700 font-medium py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-all duration-300 border border-gray-200 hover:border-blue-200"
                                title="Edit Campaign"
                              >
                                <FaEdit />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleDelete(campaign._id)}
                                className="flex-1 bg-gray-100 hover:bg-red-50 text-red-600 hover:text-red-700 font-medium py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-all duration-300 border border-gray-200 hover:border-red-200"
                                title="Delete Campaign"
                              >
                                <FaTrash />
                                <span>Delete</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          <Modal
            show={showModal}
            onClose={() => setShowModal(false)}
            title={currentCampaign ? "Edit Campaign" : "Create New Campaign"}
            size="2xl"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Details</h3>
                
                {/* Campaign name field */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Date range fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      name="date_from"
                      value={formData.date_from}
                      onChange={handleChange}
                      required
                      disabled={currentCampaign}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${currentCampaign ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    {currentCampaign && (
                      <p className="text-xs text-gray-500 mt-1">Date range cannot be modified once set</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      name="date_to"
                      value={formData.date_to}
                      onChange={handleChange}
                      required
                      disabled={currentCampaign}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${currentCampaign ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>

                {/* Points/amount fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Points earned per unit</label>
                    <input
                      type="number"
                      name="default_points"
                      value={formData.default_points}
                      onChange={handleChange}
                      required
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This is the number of points dealers will earn (e.g. 50 points)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount per unit (₹)</label>
                    <input
                      type="number"
                      name="default_amount"
                      value={formData.default_amount}
                      onChange={handleChange}
                      required
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      For every ₹X of sales (e.g. ₹10,000)
                    </p>
                  </div>
                </div>

                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-blue-700 text-sm">
                    Dealers will earn <strong>{formData.default_points}</strong> points for every <strong>₹{formData.default_amount}</strong> in sales.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {loading ? 'Saving...' : (currentCampaign ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </Modal>

          <Modal
            show={showSubSchemeModal}
            title={`Manage Stock Group Rules for ${currentCampaign?.name || ''}`}
            onClose={() => setShowSubSchemeModal(false)}
            size="xl"
          >
            {loadingSubSchemes ? (
              <div className="text-center py-4">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                <div className="mb-4 p-4 border rounded">
                  <h5 className="mb-3">Add New Stock Group Rule</h5>
                  <form onSubmit={handleAddSubScheme} className="grid grid-cols-12 gap-3">
                    <div className="col-span-6">
                      <label htmlFor="stockGroupId" className="block text-sm font-medium text-gray-700 mb-1">Stock Group</label>
                      <Select
                        id="stockGroupId"
                        name="stockGroupId"
                        value={stockGroups.find(group => group._id === subSchemeForm.stockGroupId) ?
                          { value: subSchemeForm.stockGroupId, label: getStockGroupName(subSchemeForm.stockGroupId) } : null}
                        onChange={(selectedOption) => {
                          setSubSchemeForm(prev => ({
                            ...prev,
                            stockGroupId: selectedOption ? selectedOption.value : ''
                          }));
                        }}
                        options={stockGroups.map(group => ({
                          value: group._id,
                          label: group.Name || 'Unnamed Group'
                        }))}
                        placeholder="Select Stock Group"
                        className="basic-single"
                        classNamePrefix="select"
                        isClearable
                      />
                    </div>
                    <div className="col-span-2">
                      <label htmlFor="points" className="block text-sm font-medium text-gray-700 mb-1">Points</label>
                      <input
                        type="number"
                        id="points"
                        name="points"
                        value={subSchemeForm.points}
                        onChange={handleSubSchemeChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                      <input
                        type="number"
                        id="amount"
                        name="amount"
                        value={subSchemeForm.amount}
                        onChange={handleSubSchemeChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                    <div className="col-span-2 flex items-end">
                      <button
                        type="submit"
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        disabled={loadingSubSchemes || !subSchemeForm.stockGroupId}
                      >
                        <FaPlus className="mr-2" /> Add Rule
                      </button>
                    </div>
                  </form>
                </div>

                <h5 className="mb-3 text-lg font-medium text-gray-900">Current Stock Group Rules</h5>
                {subSchemes.length === 0 ? (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <FaInfo className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          No stock group rules defined yet. Default values will be used for all stock groups.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Group</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (₹)</th>
                          <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Array.isArray(subSchemes) ? subSchemes.map(scheme => (
                          <tr key={scheme._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {scheme.stockGroupName && scheme.stockGroupName !== 'Unknown' 
                                  ? scheme.stockGroupName 
                                  : getStockGroupName(scheme.stockGroupId || scheme.stockGroups)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{scheme.points}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{scheme.amount}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex justify-center space-x-2">
                                <button
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  onClick={() => handleEditSubScheme(scheme)}
                                >
                                  <FaEdit className="mr-1" /> Edit
                                </button>
                                <button
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                  onClick={() => handleDeleteSubScheme(scheme._id)}
                                >
                                  <FaTrash className="mr-1" /> Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                              No stock group rules found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {editingSubScheme && (
                  <div className="mt-6 p-4 border rounded-md bg-gray-50" id="edit-subscheme-form">
                    <h5 className="mb-3 font-medium text-gray-900">Edit Stock Group Rule</h5>
                    <form onSubmit={handleUpdateSubScheme} className="grid grid-cols-12 gap-3">
                      <div className="col-span-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Stock Group</label>
                        <input
                          type="text"
                          value={editingSubScheme.stockGroupName || getStockGroupName(editingSubScheme.stockGroupId || editingSubScheme.stockGroups)}
                          className="w-full px-3 py-2 border border-gray-200 bg-gray-100 rounded-md text-gray-700"
                          readOnly
                          disabled
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
                        <input
                          type="number"
                          name="points"
                          value={editingSubScheme.points}
                          onChange={(e) => setEditingSubScheme({ ...editingSubScheme, points: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                        <input
                          type="number"
                          name="amount"
                          value={editingSubScheme.amount}
                          onChange={(e) => setEditingSubScheme({ ...editingSubScheme, amount: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div className="col-span-2 flex items-end">
                        <button
                          type="submit"
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <FaCheck className="mr-2" /> Update
                        </button>
                      </div>
                    </form>
                    <div className="mt-2 flex justify-end">
                      <button
                        onClick={() => setEditingSubScheme(null)}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel Edit
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Modal>

          <Modal
            show={showCompanySelectionModal}
            onClose={() => setShowCompanySelectionModal(false)}
            title="Select a Company"
            size="lg"
          >
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <FaLayerGroup className="text-white text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Multiple Companies Available</h3>
                <p className="text-gray-600">Select a company to manage campaigns for:</p>
              </div>
              
              <div className="space-y-3">
                {getCompaniesForSelection().map((company) => (
                  <button
                    key={company._id}
                    onClick={() => handleCompanySelect(company._id)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-300 transform hover:scale-[1.02] ${
                      company._id === companyID 
                        ? 'border-blue-500 bg-blue-50 shadow-lg' 
                        : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50 shadow-md hover:shadow-lg'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold text-gray-800 text-lg">{company.name}</div>
                        <div className="text-sm text-gray-500">Click to select this company</div>
                      </div>
                      {company._id === companyID && (
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <FaCheck className="text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-8 flex justify-end">
                <button
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  onClick={() => {
                    const companies = getCompaniesForSelection();
                    if (!companyID && companies && companies.length > 0) {
                      handleCompanySelect(companies[0]._id);
                    } else {
                      setShowCompanySelectionModal(false);
                    }
                  }}
                >
                  Confirm Selection
                </button>
              </div>
            </div>
          </Modal>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignManagement;